import { BANCOS_ES } from './iban-bank-data.js';

/**
 * Datos del banco a partir del IBAN.
 *
 * En un IBAN español los cuatro dígitos que van detrás del código de país y el dígito de control son la
 * **entidad** (`ES91 2100`… → 2100, CaixaBank), y de ahí sale también el BIC/SWIFT, porque cada banco
 * tiene el suyo y no cambia. Escribirlos a mano en cada cuenta es una fuente de erratas y de pagos
 * devueltos, así que se rellenan solos y se pueden corregir si el banco no está en la tabla.
 *
 * La tabla **no se ha escrito a mano**: sale del juego público de códigos de banco españoles
 * (`data/banks-es.csv`, ver `scripts/build-banks-data.mjs`). Un BIC equivocado manda el dinero a otra
 * entidad, así que un código que no esté se deja vacío en vez de adivinarlo.
 */

/** Longitud oficial del IBAN español. */
export const LONGITUD_IBAN_ES = 24;

/** BIC del banco: 8 caracteres cuando la oficina es `XXX` y 11 cuando el sufijo es de la entidad. */
export type BancoIban = { nombre: string; bic: string; entidad: string };

/**
 * Banco y BIC de un IBAN español, o `null` si no es español, no tiene la longitud del país o la entidad
 * no está en la tabla.
 */
export function bancoDelIban(iban: string): BancoIban | null {
  const limpio = iban.replace(/\s+/g, '').toUpperCase();
  if (!/^ES\d{22}$/.test(limpio)) return null;
  const entidad = limpio.slice(4, 8);
  const banco = BANCOS_ES[entidad];
  return banco ? { ...banco, entidad } : null;
}

/** `true` si el IBAN es español y tiene los 24 caracteres que le tocan. */
export function longitudIbanValida(iban: string): boolean {
  const limpio = iban.replace(/\s+/g, '').toUpperCase();
  return !limpio.startsWith('ES') || limpio.length === LONGITUD_IBAN_ES;
}
